import { AsyncDirective, directive } from "lit/async-directive.js";
import { map, Observable, Subscription } from "rxjs";
import { noChange, nothing, TemplateResult } from "lit";
import { Id, Model, Ref, takeId } from "shared";
import { modelStore } from "../state/model/model-store";

export class AsyncModelDirective<TModel extends Model> extends AsyncDirective {
	observable: Observable<unknown> | undefined;
	subscription: Subscription | undefined;
	id: Id<TModel> | undefined;

	// When the observable changes, unsubscribe to the old one and
	// subscribe to the new one
	render(id: Ref<TModel> | undefined, cb: (model: TModel) => TemplateResult | string) {
		id = takeId(id);

		if (id == null) {
			return nothing;
		} else if (this.id !== id) {
			this.id = id;
		}

		this.setObservable(modelStore.get$(id).pipe(map((model) => cb(model))));
		return noChange;
	}

	setObservable(observable: Observable<unknown>) {
		this.subscription?.unsubscribe();
		this.observable = observable;
		if (this.isConnected) {
			this.subscribe(observable);
		}
	}

	// Subscribes to the observable, calling the directive's asynchronous
	// setValue API each time the value changes
	subscribe(observable: Observable<unknown>) {
		this.subscription = observable.subscribe((v: unknown) => {
			this.setValue(v);
		});
	}
	// When the directive is disconnected from the DOM, unsubscribe to ensure
	// the directive instance can be garbage collected
	disconnected() {
		this.subscription?.unsubscribe();
	}

	// If the subtree the directive is in was disconnected and subsequently
	// re-connected, re-subscribe to make the directive operable again
	reconnected() {
		this.subscribe(this.observable!);
	}
}
export const asyncModel = directive(AsyncModelDirective) as <TModel extends Model>(
	id: Ref<TModel> | undefined | null,
	cb: (model: TModel) => TemplateResult | string | undefined | null
) => ReturnType<AsyncModelDirective<TModel>["render"]>;
